import React, { useState, useEffect, useCallback, useRef } from 'react';
import HostContactEditor from '../components/HostContactEditor';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { StatusBadge, Progress, PageLoad, ErrorMsg, Card } from '../components/ui';
import { usePrograms } from '../context/ProgramsContext';
import PandaDocModal from '../components/PandaDocModal';
import HostAgreementUploads from '../components/HostAgreementUploads';

const TERMINAL_STATUSES = ['executed', 'declined'];

function useAgreementStatus(hostId, documentId) {
  const [agreement, setAgreement] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const manualRefreshRef = useRef(null);

  useEffect(() => {
    if (!hostId) return;
    let cancelled = false;
    let timer;
    async function fetchStatus() {
      try {
        const res = await api.get(`/hosts/${hostId}/agreement`);
        if (cancelled) return;
        setAgreement(res.data);
        setLastUpdated(Date.now());
        setSecondsAgo(0);
        if (TERMINAL_STATUSES.includes(res.data.status)) return;
        timer = setTimeout(fetchStatus, 15000);
      } catch {
        if (!cancelled) timer = setTimeout(fetchStatus, 30000);
      }
    }
    manualRefreshRef.current = fetchStatus;
    fetchStatus();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [hostId, documentId]);

  useEffect(() => {
    if (!lastUpdated) return;
    const tick = setInterval(() => setSecondsAgo(Math.round((Date.now() - lastUpdated) / 1000)), 5000);
    return () => clearInterval(tick);
  }, [lastUpdated]);

  const forceRefresh = useCallback(() => { manualRefreshRef.current?.(); }, []);
  return { agreement, secondsAgo, forceRefresh };
}

const TZ = 'America/Edmonton';
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', timeZone: TZ });
}
function fmtDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', timeZone: TZ }) +
    ' ' + d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', timeZone: TZ });
}

const AGREEMENT_CFG = {
  none:        { label: 'No Agreement',  color: '#9ca3af', border: 'rgba(156,163,175,.30)', dot: '#9ca3af',  bg: 'rgba(156,163,175,.10)' },
  draft:       { label: 'Draft',         color: '#fb923c', border: 'rgba(251,146,60,.30)',  dot: '#fb923c',  bg: 'rgba(251,146,60,.10)'  },
  sent:        { label: 'Sent',          color: '#60a5fa', border: 'rgba(96,165,250,.30)',  dot: '#60a5fa',  bg: 'rgba(96,165,250,.10)'  },
  viewed:      { label: 'Viewed',        color: '#60a5fa', border: 'rgba(96,165,250,.30)',  dot: '#60a5fa',  bg: 'rgba(96,165,250,.10)'  },
  host_signed: { label: 'Awaiting Dean', color: '#c4b5fd', border: 'rgba(196,181,253,.30)', dot: '#c4b5fd',  bg: 'rgba(196,181,253,.10)' },
  executed:    { label: 'Active Partner',color: '#7BE0A5', border: 'rgba(61,192,119,.30)',  dot: '#7BE0A5',  bg: 'rgba(61,192,119,.14)'  },
  declined:    { label: 'Declined',      color: '#f87171', border: 'rgba(248,113,113,.30)', dot: '#f87171',  bg: 'rgba(248,113,113,.10)' },
};

function HeroAgreementPill({ status }) {
  const c = AGREEMENT_CFG[status] || AGREEMENT_CFG.none;
  return (
    <span className="status-pill" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      <span className="dot" style={{ background: c.dot }} />
      {c.label}
    </span>
  );
}

function recentTimestamp(status, timeline) {
  if (!timeline) return null;
  switch (status) {
    case 'declined':    return timeline.declinedAt;
    case 'executed':    return timeline.completedAt;
    case 'viewed':      return timeline.viewedAt;
    case 'sent':        return timeline.sentAt;
    case 'host_signed': return timeline.viewedAt || timeline.sentAt;
    default:            return null;
  }
}

const HOST_TEMPLATES = [
  { key: 'host_nurture',    label: 'Monthly Partner Check-In',       triggerDesc: 'Auto: monthly cron sent to all active host contacts',        getScheduled: (h) => !!h.is_active },
  { key: 'agreement_sent',  label: 'Site Agreement Notification',     triggerDesc: 'Auto: triggered when a PandaDoc agreement is dispatched',     getScheduled: (h) => h.agreement_status === 'sent' || h.agreement_status === 'host_signed' },
];

export default function HostDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { programs: allPrograms, programMap: PROGRAM_NAMES } = usePrograms();
  const [host, setHost] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emailLog, setEmailLog] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [downloadingAgreement, setDownloadingAgreement] = useState(false);

  const { agreement: agreementState, secondsAgo, forceRefresh } = useAgreementStatus(host?.id, host?.pandadoc_document_id);

  const fetchHost = useCallback(async () => {
    try {
      const [hostRes, emailLogRes] = await Promise.all([
        api.get(`/hosts/${id}`),
        api.get(`/hosts/${id}/email-log`),
      ]);
      setHost(hostRes.data);
      setEmailLog(emailLogRes.data);
    } catch {
      navigate('/hosts');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchHost(); }, [fetchHost]);

  const handleDownloadAgreement = async () => {
    setDownloadingAgreement(true);
    setError('');
    try {
      const response = await api.get(`/hosts/${id}/agreement/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${host.org_name.replace(/[^a-zA-Z0-9._-]/g, '_')}-host-agreement.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      let message = 'Could not download the agreement.';
      if (err.response?.data instanceof Blob) {
        try { message = JSON.parse(await err.response.data.text()).error || message; } catch { /* keep readable error */ }
      }
      setError(message);
    } finally {
      setDownloadingAgreement(false);
    }
  };

  const handleSave = async () => {
    try {
      const res = await api.put(`/hosts/${id}`, editForm);
      setHost({ ...res.data, students: host.students });
      setEditing(false);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update host');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/hosts/${id}`);
      navigate('/hosts');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete host');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleSendHostEmail = async (triggerType) => {
    setSendingEmail(triggerType);
    try {
      await api.post(`/hosts/${id}/send-email`, { trigger_type: triggerType });
      const [logRes, hostRes] = await Promise.all([api.get(`/hosts/${id}/email-log`), api.get(`/hosts/${id}`)]);
      setEmailLog(logRes.data);
      setHost(prev => ({ ...prev, ...hostRes.data, students: prev.students }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send email');
    } finally { setSendingEmail(null); }
  };

  const programs = allPrograms.map(p => p.code);
  const timeline = agreementState?.timeline || {};

  if (loading) return <PageLoad />;
  if (!host) return <div className="page" style={{ color: 'var(--ink-3)' }}>Host not found</div>;

  const orgInitials = host.org_name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const canSendAgreement = !agreementState?.status || ['none', 'draft', 'declined'].includes(agreementState.status);
  const agreementStatus = agreementState?.status || host.agreement_status || 'none';

  const lastUpdatedLabel = secondsAgo < 10 ? 'just now' : secondsAgo < 60 ? `${secondsAgo}s ago` : `${Math.floor(secondsAgo / 60)}m ago`;
  const isTerminal = TERMINAL_STATUSES.includes(agreementStatus);

  return (
    <div className="page">
      {error && <ErrorMsg>{error}</ErrorMsg>}

      {/* ─── PROFILE HERO ─── */}
      <section className="hero">
        <div className="hero-topo" aria-hidden="true">
          <svg viewBox="0 0 1200 240" preserveAspectRatio="xMidYMid slice">
            <defs>
              <pattern id="hdp-dotg" width="24" height="24" patternUnits="userSpaceOnUse">
                <circle cx="12" cy="12" r="0.6" fill="rgba(255,255,255,0.10)" />
              </pattern>
            </defs>
            <rect width="1200" height="240" fill="url(#hdp-dotg)" />
            <g fill="none" stroke="rgba(245,166,35,0.30)" strokeWidth="1.4" strokeLinejoin="round">
              <path d="M-50 200 L 150 110 L 300 160 L 500 70 L 700 150 L 900 90 L 1100 160 L 1300 100" />
            </g>
            <g fill="none" stroke="rgba(245,166,35,0.16)" strokeWidth="1.1" strokeLinejoin="round">
              <path d="M-50 230 L 150 160 L 300 210 L 500 130 L 700 200 L 900 150 L 1100 210 L 1300 160" />
            </g>
            <circle cx="500" cy="70" r="14" fill="rgba(245,166,35,0.35)" />
            <circle cx="500" cy="70" r="3" fill="#FBC559" />
          </svg>
        </div>
        <div className="hero-glow" />
        <div className="hero-inner">
          <div className="hero-avatar">{orgInitials}</div>
          <div className="hero-mid">
            <div className="hero-eyebrow">
              Host Organization{host.campus_region ? ` · ${host.campus_region}` : ''}
            </div>
            <h1 className="hero-name">
              {host.org_name}
              <HeroAgreementPill status={agreementStatus} />
            </h1>
            <div className="hero-meta">
              {host.contact_email && (
                <a href={`mailto:${host.contact_email}`}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-1px', marginRight: 5 }}><path d="M4 4h16v16H4z" /><path d="M22 6l-10 7L2 6" /></svg>
                  {host.contact_email}
                </a>
              )}
              {host.contact_email && host.contact_phone && <span className="sep" />}
              {host.contact_phone && (
                <a href={`tel:${host.contact_phone}`}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-1px', marginRight: 5 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 2 4.18 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.72 13 13 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11l-1.27 1.27a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 13 13 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                  {host.contact_phone}
                </a>
              )}
              {host.website && (host.contact_email || host.contact_phone) && <span className="sep" />}
              {host.website && (
                <a href={host.website} target="_blank" rel="noopener noreferrer">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-1px', marginRight: 5 }}><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  {host.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
              )}
            </div>
          </div>
          {user.role === 'admin' && (
            <div className="hero-actions">
              {canSendAgreement && (
                <button className="btn-hero btn-hero-edit" onClick={() => setShowModal(true)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>
                  Send Agreement
                </button>
              )}
              <button
                className="btn-hero btn-hero-edit"
                onClick={() => { setEditing(!editing); setEditForm(host); setError(''); }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></svg>
                {editing ? 'Cancel' : 'Edit'}
              </button>
              <button className="btn-hero btn-hero-delete" onClick={() => setShowDeleteConfirm(true)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                Delete
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ─── EDIT FORM ─── */}
      {!editing && (user.role === 'admin' || user.is_host_specialist) && (
        <Card title="Contact details" style={{ marginTop: 18 }}>
          <HostContactEditor key={host.id} host={host} onSaved={fetchHost} />
        </Card>
      )}

      {editing && (
        <Card title="Edit Host" style={{ marginTop: 18, marginBottom: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            <EField label="Organization Name" value={editForm.org_name || ''} onChange={v => setEditForm({ ...editForm, org_name: v })} />
            <EField label="First Name" value={editForm.contact_first_name || ''} onChange={v => setEditForm({ ...editForm, contact_first_name: v })} />
            <EField label="Last Name" value={editForm.contact_last_name || ''} onChange={v => setEditForm({ ...editForm, contact_last_name: v })} />
            <EField label="Contact Position" value={editForm.contact_position || ''} onChange={v => setEditForm({ ...editForm, contact_position: v })} placeholder="e.g. HR Manager" />
            <EField label="Contact Email" type="email" value={editForm.contact_email || ''} onChange={v => setEditForm({ ...editForm, contact_email: v })} />
            <EField label="Phone" value={editForm.contact_phone || ''} onChange={v => setEditForm({ ...editForm, contact_phone: v })} />
            <EField label="Capacity" type="number" value={editForm.capacity || ''} onChange={v => setEditForm({ ...editForm, capacity: parseInt(v) || null })} />
            <EField label="Street Address" value={editForm.address || ''} onChange={v => setEditForm({ ...editForm, address: v })} placeholder="e.g. 4774 Westwinds Dr NE" />
            <EField label="City" value={editForm.city || ''} onChange={v => setEditForm(f => ({ ...f, city: v, campus_region: v || f.campus_region }))} placeholder="e.g. Calgary" />
            <EField label="Province" value={editForm.province || ''} onChange={v => setEditForm({ ...editForm, province: v })} placeholder="e.g. Alberta" />
            <EField label="Postal Code" value={editForm.postal_code || ''} onChange={v => setEditForm({ ...editForm, postal_code: v })} placeholder="e.g. T2P 1G1" />
            <div style={{ gridColumn: '1 / -1' }}>
              <EField label="Website" value={editForm.website || ''} onChange={v => setEditForm({ ...editForm, website: v })} placeholder="https://" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 5 }}>Notes</label>
              <textarea className="input" rows={3} style={{ resize: 'none' }} value={editForm.notes || ''} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 6 }}>Programs Accepted</label>
              <div className="row gap-16">
                {programs.map(p => (
                  <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={(editForm.programs_accepted || []).includes(p)}
                      onChange={() => {
                        const current = editForm.programs_accepted || [];
                        setEditForm({ ...editForm, programs_accepted: current.includes(p) ? current.filter(x => x !== p) : [...current, p] });
                      }} />
                    {PROGRAM_NAMES[p] || p}
                  </label>
                ))}
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={editForm.is_active ?? true} onChange={e => setEditForm({ ...editForm, is_active: e.target.checked })} />
              Active host
            </label>
            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--line-2)', paddingTop: 14, marginTop: 2 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 6 }}>Student Acceptance</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                Managed on the <strong>Hosts</strong> page — open this host and use the “Accepting Students” panel. Turning a host off requires Host Specialist confirmation.
              </div>
            </div>
          </div>
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={() => { setEditing(false); setError(''); }}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave}>Save Changes</button>
          </div>
        </Card>
      )}

      {/* ─── INFO GRID ─── */}
      <section className="info-grid">
        {[
          { label: 'Contact',        value: [host.contact_first_name, host.contact_last_name].filter(Boolean).join(' ') || '—',
                                     muted: !(host.contact_first_name || host.contact_last_name) },
          { label: 'Position',       value: host.contact_position || '—',     muted: !host.contact_position },
          { label: 'City',           value: host.city || host.campus_region || '—', muted: !(host.city || host.campus_region) },
          { label: 'Province',       value: host.province || '—',             muted: !host.province },
          { label: 'Programs',       value: host.programs_accepted?.map(p => PROGRAM_NAMES[p] || p).join(', ') || '—',
                                     muted: !host.programs_accepted?.length },
          { label: 'Capacity',       value: host.capacity ? `${host.capacity} students` : '—', muted: !host.capacity },
          { label: 'Agreement Sent', value: fmtDate(timeline.sentAt || host.agreement_sent_date), muted: !(timeline.sentAt || host.agreement_sent_date) },
          { label: 'Agreement Executed', value: fmtDate(timeline.completedAt || host.agreement_executed_date), muted: !(timeline.completedAt || host.agreement_executed_date) },
        ].map(cell => (
          <div key={cell.label} className="info-cell">
            <span className="info-label"><span className="pip" />{cell.label}</span>
            <span className={`info-val${cell.muted ? ' muted' : ''}`}>{cell.value}</span>
          </div>
        ))}
      </section>

      {/* ─── DETAILS + AGREEMENT GRID ─── */}
      <div className="sdp-grid">

        {/* Host Details */}
        <section className="sdp-card">
          <div className="card-head">
            <div>
              <span className="card-eyebrow">Contact</span>
              <h2 className="card-title">Host Details</h2>
            </div>
            <span className="card-foot-note">
              {host.is_active ? 'Active partner' : 'Inactive'}
            </span>
          </div>
          <div className="req-list">
            {[
              { label: 'Address',      value: host.address || '—' },
              { label: 'Postal Code',  value: host.postal_code || '—' },
              { label: 'Avg Capacity', value: host.capacity ? `${host.capacity} students` : '—' },
              { label: 'Last Nurture', value: fmtDate(host.last_nurture_email_sent) || 'Never sent' },
              { label: 'Availability', value: host.availability_confirmed ? 'Confirmed' : 'Not confirmed' },
              {
                label: 'Accepting',
                value: host.accepting_updated_at
                  ? `${host.accepting_students
                      ? `Yes${host.accepting_capacity ? ` (${host.accepting_capacity} spot${host.accepting_capacity !== 1 ? 's' : ''})` : ''}`
                      : 'No'} · as of ${fmtDate(host.accepting_updated_at)}`
                  : '—',
              },
            ].map(row => (
              <div key={row.label} className="req-row static">
                <span style={{ fontSize: 12.5, color: 'var(--ink-3)', fontWeight: 600 }}>{row.label}</span>
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{row.value}</span>
              </div>
            ))}
          </div>
          {host.notes && (
            <div style={{ marginTop: 14, padding: '12px 0 0', borderTop: '1px solid var(--line-2)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', color: 'var(--ink-3)', textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{host.notes}</div>
            </div>
          )}
        </section>

        {/* Agreement Status */}
        <section className="sdp-card">
          <div className="card-head">
            <div>
              <span className="card-eyebrow">PandaDoc</span>
              <h2 className="card-title">Site Agreement</h2>
            </div>
            {agreementState && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  {isTerminal ? 'Final' : `Updated ${lastUpdatedLabel}`}
                </span>
                <button onClick={forceRefresh} title="Refresh" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 2, display: 'flex', alignItems: 'center' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                </button>
              </div>
            )}
          </div>

          {/* Status pill */}
          <div style={{ marginBottom: 16 }}>
            {(() => {
              const cfg = AGREEMENT_CFG[agreementStatus] || AGREEMENT_CFG.none;
              const ts = recentTimestamp(agreementStatus, agreementState?.timeline);
              return (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 20, padding: '6px 14px' }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: cfg.color }}>{cfg.label}</span>
                  {ts && <span style={{ fontSize: 11, color: cfg.color, opacity: 0.75 }}>· {fmtDateTime(ts)}</span>}
                </div>
              );
            })()}
          </div>

          {/* Timeline */}
          <div className="req-list" style={{ gap: 0 }}>
            {[
              { label: 'Sent',     value: fmtDate(timeline.sentAt     || host.agreement_sent_date) },
              { label: 'Viewed',   value: fmtDate(timeline.viewedAt) },
              { label: 'Executed', value: fmtDate(timeline.completedAt || host.agreement_executed_date) },
              { label: 'Expires',  value: fmtDate(timeline.expiresAt  || host.agreement_expires_date) },
            ].map(row => (
              <div key={row.label} className="req-row" style={{ cursor: 'default', padding: '9px 0' }}>
                <span style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600, minWidth: 80 }}>{row.label}</span>
                <span style={{ fontSize: 13, color: row.value === '—' ? 'var(--ink-4)' : 'var(--ink-2)' }}>{row.value}</span>
              </div>
            ))}
          </div>

          {agreementState?.viewUrl && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line-2)' }}>
              <button className="btn btn-primary btn-sm" onClick={handleDownloadAgreement}
                disabled={downloadingAgreement} style={{ marginRight: 12 }}>
                {downloadingAgreement ? 'Downloading…' : 'Download agreement PDF'}
              </button>
              <a href={agreementState.viewUrl} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 13, color: 'var(--navy)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600 }}>
                View in PandaDoc
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                  <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            </div>
          )}

          {user.role === 'admin' && canSendAgreement && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line-2)' }}>
              <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
                Send Site Agreement
              </button>
            </div>
          )}
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--line-2)' }}>
            <HostAgreementUploads
              key={host.id}
              hostId={host.id}
              canManage={user.role === 'admin' || !!user.is_host_specialist}
              compact
            />
          </div>
        </section>
      </div>

      {/* ─── EMAIL COMMUNICATIONS ─── */}
      <Card
        title="Email Communications"
        sub={host.contact_email ? `Emails sent to ${host.contact_email}` : 'No contact email configured'}
        padded={false}
        action={<span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{emailLog.length} total sent</span>}
        style={{ marginTop: 18, marginBottom: 18 }}
      >
        {HOST_TEMPLATES.map(tmpl => {
          const lastSent = emailLog.filter(e => e.trigger_type === tmpl.key)?.[0]?.sent_at;
          const count = emailLog.filter(e => e.trigger_type === tmpl.key).length;
          const scheduled = tmpl.getScheduled(host);
          const isSending = sendingEmail === tmpl.key;
          return (
            <div key={tmpl.key} className="email-row">
              <div>
                <div className="name">
                  {tmpl.label}
                  {scheduled && <span className="badge active" style={{ marginLeft: 8, fontSize: 10 }}>Scheduled</span>}
                  {count > 0 && <span className="badge muted" style={{ marginLeft: 4, fontSize: 10 }}>Sent {count}×</span>}
                </div>
                <div className="trig">{tmpl.triggerDesc}</div>
              </div>
              <div className="sent">
                {lastSent ? <div>Last: {fmtDateTime(lastSent)}</div> : <span>Never sent</span>}
              </div>
              <button className="btn btn-secondary btn-sm" disabled={!!sendingEmail || !host.contact_email}
                title={!host.contact_email ? 'No contact email configured' : 'Send now'}
                onClick={() => handleSendHostEmail(tmpl.key)}>
                {isSending ? <span>Sending…</span> : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    Send
                  </>
                )}
              </button>
            </div>
          );
        })}
      </Card>

      {/* ─── ASSIGNED STUDENTS ─── */}
      {host.students?.length > 0 && (
        <section className="sdp-card" style={{ marginBottom: 18 }}>
          <div className="card-head">
            <div>
              <span className="card-eyebrow">Placements</span>
              <h2 className="card-title">Assigned Students</h2>
            </div>
            <span className="card-foot-note">{host.students.length} student{host.students.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="table-wrap" style={{ margin: '0 -22px -22px' }}>
            <table className="tbl">
              <thead>
                <tr><th>Name</th><th>Program</th><th>Status</th><th style={{ width: 200 }}>Hours</th></tr>
              </thead>
              <tbody>
                {host.students.map(s => (
                  <tr key={s.id} className="row-link" onClick={() => navigate(`/students/${s.id}`)}>
                    <td className="nm"><Link to={`/students/${s.id}`} style={{ color: 'inherit' }}>{s.first_name} {s.last_name}</Link></td>
                    <td className="muted">{s.program_name || s.program_code}</td>
                    <td><StatusBadge status={s.status} /></td>
                    <td><Progress value={parseFloat(s.hours_logged)} max={s.hours_required} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ─── PANDADOC MODAL ─── */}
      {showModal && (
        <PandaDocModal
          host={host}
          programs={allPrograms.filter(p => (host.programs_accepted || []).includes(p.code))}
          onClose={() => setShowModal(false)}
          onSent={() => { setShowModal(false); fetchHost(); forceRefresh(); }}
        />
      )}

      {/* ─── DELETE CONFIRM MODAL ─── */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                </svg>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--ink-1)' }}>Delete Host</div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>This action cannot be undone</div>
              </div>
            </div>
            <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: 24 }}>
              Are you sure you want to permanently delete <strong>{host.org_name}</strong>?
              Any students currently assigned to this host will be unassigned.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</button>
              <button className="btn" style={{ background: '#ef4444', color: '#fff', border: 'none' }} onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Yes, delete host'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EField({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 5 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="input" placeholder={placeholder} />
    </div>
  );
}
